import { useCallback, useMemo, useState } from 'react';
import { type InfiniteData, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEventManagementService } from '../services/eventManagementService';
import { actionLogKeys } from './actionLogKeys';
import { useLinkedActionItem } from './useLinkedActionItem';
import type { ActionLogEntry, ActionLogResponse } from '../../../types/backend';

const INCIDENT_PAGE_SIZE = 50;
const REPORT_PAGE_SIZE = 25;

function mergeByOpsItem(...lists: ActionLogEntry[][]): ActionLogEntry[] {
  const byId = new Map<string, ActionLogEntry>();
  for (const list of lists) {
    for (const entry of list) byId.set(entry.opsitem_id, entry);
  }
  return Array.from(byId.values()).sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
}

interface UseActionLog {
  entries: ActionLogEntry[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  setError: (e: string | null) => void;
  reload: () => void;
  loadMore: () => void;
  loadEntry: (opsitemId: string) => Promise<ActionLogEntry | null>;
  closeItem: (opsitemId: string) => Promise<void>;
  deleteItem: (opsitemId: string) => Promise<void>;
}

/**
 * Loads the full action log for a customer: tracked incidents (paginated) plus
 * rendered reports (a separate report-scoped read), merged into one stream so the
 * view can filter and facet across every kind client-side. Writes patch the cached
 * pages in place — never invalidate, which would drop the accumulated scroll.
 */
export function useActionLog(customerId: string): UseActionLog {
  const eventService = useEventManagementService();
  const queryClient = useQueryClient();
  const [localError, setLocalError] = useState<string | null>(null);
  const [dismissedAt, setDismissedAt] = useState(0);

  const incidentsQuery = useInfiniteQuery({
    queryKey: actionLogKeys.incidents(customerId),
    queryFn: ({ pageParam }) => eventService.getActionLog(customerId, { max_results: INCIDENT_PAGE_SIZE, next_token: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_token ?? undefined,
  });

  const reportsQuery = useQuery({
    queryKey: actionLogKeys.reports(customerId),
    queryFn: () => eventService.getActionLog(customerId, { max_results: REPORT_PAGE_SIZE, reports_only: true }),
  });

  // A dismissible error: writes set it directly; a load failure is surfaced from the
  // queries but suppressed once dismissed (by its errorUpdatedAt) so setError(null)
  // actually clears the banner — a fresh failure re-shows it.
  // Track the *most recent* of the two failures: preferring incidents unconditionally
  // would let an older, already-dismissed incidents error swallow a fresh reports one,
  // since the dismissal is keyed on the surfaced errorUpdatedAt.
  const incidentsErrorAt = incidentsQuery.isError ? incidentsQuery.errorUpdatedAt : 0;
  const reportsErrorAt = reportsQuery.isError ? reportsQuery.errorUpdatedAt : 0;
  const loadErrorAt = Math.max(incidentsErrorAt, reportsErrorAt);
  const loadError = reportsErrorAt > incidentsErrorAt ? reportsQuery.error : incidentsQuery.error;
  const setError = useCallback((value: string | null) => {
    if (value === null) setDismissedAt(loadErrorAt);
    setLocalError(value);
  }, [loadErrorAt]);

  const patchIncidents = useCallback((fn: (items: ActionLogEntry[]) => ActionLogEntry[]) => {
    queryClient.setQueryData<InfiniteData<ActionLogResponse>>(
      actionLogKeys.incidents(customerId),
      (old) => (old ? { ...old, pages: old.pages.map((p) => ({ ...p, items: fn(p.items) })) } : old),
    );
  }, [queryClient, customerId]);

  const patchReports = useCallback((fn: (items: ActionLogEntry[]) => ActionLogEntry[]) => {
    queryClient.setQueryData<ActionLogResponse>(
      actionLogKeys.reports(customerId),
      (old) => (old ? { ...old, items: fn(old.items) } : old),
    );
  }, [queryClient, customerId]);

  const reload = useCallback(() => {
    setError(null);
    queryClient.invalidateQueries({ queryKey: actionLogKeys.incidents(customerId) });
    queryClient.invalidateQueries({ queryKey: actionLogKeys.reports(customerId) });
  }, [queryClient, customerId, setError]);

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = incidentsQuery;
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { linkedEntry, loadEntry, patchLinked } = useLinkedActionItem({
    customerId,
    firstPageSettled: incidentsQuery.isSuccess && !incidentsQuery.isFetching,
    dataUpdatedAt: incidentsQuery.dataUpdatedAt,
    patchReports,
    setError,
  });

  // Memoised so the merge and re-sort of every loaded entry run only when a query's data changes, and the
  // tab's filtered list and selection see a stable array between those changes.
  const entries = useMemo(() => {
    const incidents = incidentsQuery.data?.pages.flatMap((p) => p.items) ?? [];
    const reports = reportsQuery.data?.items ?? [];
    return mergeByOpsItem(incidents, reports, linkedEntry ? [linkedEntry] : []);
  }, [linkedEntry, incidentsQuery.data, reportsQuery.data]);

  const closeItem = useCallback(async (opsitemId: string) => {
    setError(null);
    await eventService.closeActionLogItem(customerId, opsitemId);
    const resolve = (items: ActionLogEntry[]) => items.map((e) => (e.opsitem_id === opsitemId ? { ...e, status: 'Resolved' } : e));
    patchIncidents(resolve);
    patchReports(resolve);
    patchLinked((entry) => resolve([entry])[0]);
  }, [customerId, eventService, patchIncidents, patchReports, patchLinked, setError]);

  const deleteItem = useCallback(async (opsitemId: string) => {
    setError(null);
    await eventService.deleteActionLogItem(customerId, opsitemId);
    const remove = (items: ActionLogEntry[]) => items.filter((e) => e.opsitem_id !== opsitemId);
    patchIncidents(remove);
    patchReports(remove);
    patchLinked((entry) => (entry.opsitem_id === opsitemId ? null : entry));
  }, [customerId, eventService, patchIncidents, patchReports, patchLinked, setError]);

  const loadErrorMessage = loadErrorAt && loadErrorAt !== dismissedAt
    ? (loadError instanceof Error ? loadError.message : 'Failed to load action log')
    : null;

  return {
    entries,
    // Reflects the first load AND a manual reload/refetch (isRefetching), but not
    // pagination (isFetchingNextPage drives loadingMore).
    loading: incidentsQuery.isLoading || reportsQuery.isLoading || incidentsQuery.isRefetching || reportsQuery.isRefetching,
    loadingMore: isFetchingNextPage,
    error: localError ?? loadErrorMessage,
    hasMore: hasNextPage ?? false,
    setError,
    reload,
    loadMore,
    loadEntry,
    closeItem,
    deleteItem,
  };
}
